import {
  useContext,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import './createMeme.css';
import axios from 'axios';
import axiosConn from '../../axiosConn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Cancel } from '@material-ui/icons';
import LoadingBar from 'react-top-loading-bar';
import { useMediaQuery } from 'react-responsive';
import { AuthContext } from '../../context/AuthContext';
import { CircularProgress } from '@material-ui/core';
import { SocketContext } from '../../context/socket';
import { useConfettiText } from '../../hooks/useConfettiText';
import { MAX_VIDEO_SIZE, MAX_IMAGE_SIZE } from '../../config/constants';
import { useTheme } from '../../context/ThemeContext';
import {
  createMediaUploader,
  handleUploadError,
} from '../../utils/mediaUpload';
import PlayerTagPicker from '../playerTagPicker/PlayerTagPicker';

const MESSAGE_PARTICLE_COUNT = 1000;

// Broad extension sets (easily extendable)
const IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'avif',
  'heic',
  'heif',
  'bmp',
  'tiff',
  'svg',
];
const VIDEO_EXTENSIONS = [
  'mp4',
  'm4v',
  'mov',
  'webm',
  'ogg',
  'ogv',
  'flv',
  'mkv',
  'avi',
  '3gp',
  '3g2',
];

// Broad host patterns (loosely allow “anywhere” but still curated extras)
const MEDIA_HOST_PATTERNS = [
  /imgur\.com/i,
  /giphy\.com/i,
  /tenor\.com/i,
  /cdn\./i,
  /cloudfront\.net/i,
  /googleusercontent\.com/i,
  /akamaihd\.net/i,
  /fbcdn\.net/i,
  /twimg\.com/i,
  /media\.amazonaws\.com/i,
  /dropboxusercontent\.com/i,
  /streamable\.com/i,
  /wistia\.com/i,
  /dailymotion\.com/i,
  /vimeo\.com/i,
  /youtube\.com/i,
  /youtu\.be/i,
  /twitch\.tv/i,
  /kaltura\.com/i,
  /vidyard\.com/i,
  /soundcloud\.com/i, // audio (still allow)
];

// Simple URL test utilities
const isHttpUrl = (val) => /^https?:\/\//i.test(val.trim());

const extractExtension = (url) => {
  try {
    const u = new URL(url);
    const path = u.pathname.split('?')[0];
    const seg = path.split('/').pop() || '';
    const match = seg.match(/\.([a-z0-9]+)$/i);
    return match ? match[1].toLowerCase() : '';
  } catch {
    return '';
  }
};

const isMediaFileExtension = (ext) =>
  IMAGE_EXTENSIONS.includes(ext) || VIDEO_EXTENSIONS.includes(ext);

const hostMatchesPattern = (url) => {
  try {
    const host = new URL(url).host;
    return MEDIA_HOST_PATTERNS.some((re) => re.test(host));
  } catch {
    return false;
  }
};

// Accept remote meme if:
// 1. Valid http(s) AND (file extension in known lists OR host matches known media/stream/video platforms)
// This “anywhere” approach errs on permissive while giving structured feedback.
const isSupportedRemoteMediaUrl = (url) => {
  if (!isHttpUrl(url)) return false;
  const ext = extractExtension(url);
  if (isMediaFileExtension(ext)) return true;
  if (hostMatchesPattern(url)) return true;
  return false;
};

// Heuristic: show inline preview if direct image extension; video tag if direct video extension.
// Otherwise embed left to front-end consumption (e.g. YouTube player downstream).
const getRemotePreviewType = (url) => {
  const ext = extractExtension(url);
  if (IMAGE_EXTENSIONS.includes(ext)) return 'image';
  if (VIDEO_EXTENSIONS.includes(ext)) return 'video';
  return 'none';
};

const VIDEO_FILE_REGEX = /\.(mp4|m4v|mov|webm|ogg|ogv|flv|mkv|avi|3gp|3g2)$/i;

const formatBytes = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return '0 B';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const getHostLabel = (url) => {
  try {
    return new URL(url).host || 'remote-host';
  } catch {
    return 'remote-host';
  }
};

export default function CreateMeme({ setMemeItems }) {
  const { player } = useContext(AuthContext);
  const { socket: socket2 } = useContext(SocketContext);
  const { darkTheme } = useTheme();
  const mobileView = useMediaQuery({ query: '(max-width: 805px)' });

  // Form refs
  const urlInputRef = useRef(null);
  const titleRef = useRef(null);
  const fileInputRef = useRef(null);
  const memeRef = useRef(null);

  // State
  const [mode, setMode] = useState('idle'); // 'idle' | 'file' | 'url'
  const [file, setFile] = useState(null);
  const [remoteUrl, setRemoteUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [linearProgress, setLinearProgress] = useState(0);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [filePreviewUrl, setFilePreviewUrl] = useState('');
  const [filePreviewType, setFilePreviewType] = useState('none');
  const [selectedTaggedPlayers, setSelectedTaggedPlayers] = useState([]);

  // Confetti
  const [showConfettiText, ConfettiTextComponent] = useConfettiText(
    memeRef,
    mobileView,
    '+10 JGK Win!',
    MESSAGE_PARTICLE_COUNT,
    'meme',
  );

  // Uploader (singleton)
  const uploaderRef = useRef(null);
  if (!uploaderRef.current) {
    uploaderRef.current = createMediaUploader({
      axiosInstance: axios,
      maxImageBytes: MAX_IMAGE_SIZE,
      maxVideoBytes: MAX_VIDEO_SIZE,
      chunkThresholdBytes: 90 * 1024 * 1024,
      chunkSizeBytes: 20 * 1024 * 1024,
      // Cloud config via env or defaults inside util
    });
  }
  const uploader = uploaderRef.current;

  // Auto-hide success message
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 3200);
    return () => clearTimeout(t);
  }, [message]);

  // Derived
  const disableUrlField = mode === 'file';
  const disableFileInput = mode === 'url';
  const remotePreviewType = useMemo(
    () => getRemotePreviewType(remoteUrl),
    [remoteUrl],
  );
  const remoteHost = useMemo(() => getHostLabel(remoteUrl), [remoteUrl]);

  const resetForm = useCallback(() => {
    setMode('idle');
    setFile(null);
    setRemoteUrl('');
    setUploadPct(0);
    setLinearProgress(0);
    setError('');
    setSelectedTaggedPlayers([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (urlInputRef.current) urlInputRef.current.value = '';
    if (titleRef.current) titleRef.current.value = '';
  }, []);

  const handleRemoteUrlChange = useCallback((e) => {
    const val = e.target.value;
    setRemoteUrl(val);
    if (val.trim().length) {
      setMode('url');
    } else {
      setMode((prev) => (prev === 'url' ? 'idle' : prev));
    }
    if (val.trim().length) {
      setError('');
    }
  }, []);

  const validateRemoteUrl = () => {
    if (!remoteUrl.trim()) {
      setError('Enter a meme URL or choose a file.');
      return false;
    }
    if (!isSupportedRemoteMediaUrl(remoteUrl.trim())) {
      setError('Unsupported URL or media type.');
      return false;
    }
    return true;
  };

  const handleFileSelect = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError('');
    try {
      uploader.validate(f); // size + type
      setFile(f);
      setMode('file');
      setRemoteUrl('');
      if (urlInputRef.current) urlInputRef.current.value = '';
    } catch (err) {
      setError(err.message || `${f.name} not supported.`);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setMode((prev) => (prev === 'file' ? 'idle' : prev));
    }
  }, [uploader]);

  // Drag & drop for file
  const dndRef = useRef(null);
  const onDragOver = (e) => {
    e.preventDefault();
    dndRef.current?.classList.add('memeDndHover');
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dndRef.current?.classList.remove('memeDndHover');
  };
  const onDrop = (e) => {
    e.preventDefault();
    dndRef.current?.classList.remove('memeDndHover');
    const f = e.dataTransfer.files?.[0];
    if (f) {
      handleFileSelect({ target: { files: [f] } });
    }
  };

  // Keep preview object URL lifecycle safe for repeated file swaps.
  useEffect(() => {
    if (!file) {
      setFilePreviewUrl('');
      setFilePreviewType('none');
      return undefined;
    }
    const previewUrl = URL.createObjectURL(file);
    const isVideo =
      (typeof file.type === 'string' && file.type.startsWith('video/')) ||
      VIDEO_FILE_REGEX.test(file.name || '');
    setFilePreviewUrl(previewUrl);
    setFilePreviewType(isVideo ? 'video' : 'image');
    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [file]);

  const buildMemePayload = (imgUrl) => ({
    img: imgUrl,
    title: (titleRef.current?.value || '').trim(),
    playerId: player.data.player.playerId,
    ...(selectedTaggedPlayers.length
      ? { taggedPlayerIds: selectedTaggedPlayers.map((tag) => tag._id) }
      : {}),
  });

  const sendMemeToBackend = async (payload) => {
    const res = await axiosConn.post('memes', payload, {
      headers: { Authorization: `Bearer ${player.token}` },
    });
    setMemeItems((prev) => [...prev, res.data.data.data]);
    try {
      socket2.emit('sendMeme', { meme: res.data.data.data });
    } catch {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setError('');
    setMessage('');
    setIsLoading(true);

    // Validate title
    if (!titleRef.current?.value.trim()) {
      setError('Provide a meme title.');
      setIsLoading(false);
      return;
    }

    // File Mode
    if (mode === 'file') {
      if (!file) {
        setError('Select a file.');
        setIsLoading(false);
        return;
      }
      try {
        uploader.validate(file); // re-check
      } catch (err) {
        setError(err.message || 'Invalid file.');
        setIsLoading(false);
        return;
      }

      try {
        const { transformedUrl } = await uploader.upload(file, {
          publicIdPrefix: 'Memes',
          onProgress: (pct) => {
            setUploadPct(pct);
            setLinearProgress(pct);
          },
        });
        await sendMemeToBackend(buildMemePayload(transformedUrl));
        setMessage('Success! Your meme was added.');
        showConfettiText();
        resetForm();
      } catch (err) {
        handleUploadError(err); // <-- Use the friendly error handler here

        setError(
          err.message === 'Upload canceled'
            ? 'Upload canceled.'
            : err.message || 'Upload failed.',
        );
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // URL Mode
    if (mode === 'url') {
      if (!validateRemoteUrl()) {
        setIsLoading(false);
        return;
      }
      try {
        await sendMemeToBackend(buildMemePayload(remoteUrl.trim()));
        setMessage('Success! Your meme was added.');
        showConfettiText();
        resetForm();
      } catch (err) {
        setError(err.message || 'Failed to submit meme.');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Idle (nothing selected)
    setError('Add a meme URL or choose a file.');
    setIsLoading(false);
  };

  const removeSelectedFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (mode === 'file') setMode('idle');
    setUploadPct(0);
    setLinearProgress(0);
  };

  const handleComposerPaste = useCallback(
    (e) => {
      if (isLoading) return;
      const item = Array.from(e.clipboardData?.items || []).find((i) =>
        i.type.startsWith('image/'),
      );
      if (!item) return;
      const blob = item.getAsFile();
      if (!blob) return;
      e.preventDefault();
      handleFileSelect({ target: { files: [blob] } });
    },
    [handleFileSelect, isLoading],
  );

  const activateFileMode = useCallback(() => {
    if (isLoading) return;
    setError('');
    setRemoteUrl('');
    if (urlInputRef.current) urlInputRef.current.value = '';
    setMode('file');
    fileInputRef.current?.click();
  }, [isLoading]);

  const activateUrlMode = useCallback(() => {
    if (isLoading) return;
    setError('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploadPct(0);
    setLinearProgress(0);
    setMode('url');
    if (urlInputRef.current) urlInputRef.current.focus();
  }, [isLoading]);

  const className = darkTheme
    ? mode === 'file'
      ? 'createMemeShareDarkTheme'
      : 'createMemeShareNoFileDarkTheme'
    : mode === 'file'
    ? 'createMemeShare'
    : 'createMemeShareNoFile';

  return (
    <div className="createMemeFeed">
      <LoadingBar
        color="limegreen"
        progress={linearProgress}
        onLoaderFinished={() => setLinearProgress(0)}
        shadow={false}
        height={3}
        style={{ visibility: linearProgress > 0 ? 'visible' : 'hidden' }}
      />
      <div className="createMemeFeedWrapper">
        <div
          className={className}
          ref={dndRef}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <div className="createMemeShareWrapper">
            <div className="createMemeShareTop">
              <img
                className="memeProfileImg"
                src={
                  player.data.player.profilePhoto
                    ? player.data.player.profilePhoto
                    : 'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1656194537/Images/noAvatar_tsgpm9.png'
                }
                alt=""
                draggable="false"
              />
              <span
                className={
                  darkTheme
                    ? 'app dark-theme-secondary createMemeDesc'
                    : 'app createMemeDesc'
                }
              >
                Share a Dank Meme.
                <div className="memeTooltip">
                  <InfoOutlinedIcon
                    className="sidebarIconTooltip"
                    fontSize="small"
                  />
                  <span className="memeTooltiptext">
                    <p>Use a file or a remote URL (image or video).</p>
                    <br />
                    <p>Paste images (Ctrl+V) or drag & drop them here.</p>
                    <br />
                    <p>
                      Automatic optimization & chunked upload for large videos.
                    </p>
                    <br />
                    <p>Click "Dale" to publish.</p>
                  </span>
                </div>
              </span>
              <form
                className={
                  darkTheme
                    ? 'app createMemeBox dark-theme'
                    : 'app createMemeBox'
                }
                onSubmit={handleSubmit}
                onPaste={handleComposerPaste}
              >
                <div className="memeSourceToggle">
                  <button
                    type="button"
                    className={`memeSourceBtn${
                      mode === 'file' ? ' memeSourceBtnActive' : ''
                    }`}
                    onClick={activateFileMode}
                    disabled={isLoading}
                  >
                    Upload file
                  </button>
                  <button
                    type="button"
                    className={`memeSourceBtn${
                      mode === 'url' ? ' memeSourceBtnActive' : ''
                    }`}
                    onClick={activateUrlMode}
                    disabled={isLoading}
                  >
                    Paste URL
                  </button>
                </div>
                <div className="memeInputShell">
                  <span className="memeInputShellIcon">URL</span>
                  <input
                    placeholder="Meme URL (any image/video host or platform)"
                    type="text"
                    className="createMemeInput memeInputUrl"
                    ref={urlInputRef}
                    value={remoteUrl}
                    disabled={disableUrlField || isLoading}
                    onChange={handleRemoteUrlChange}
                    onPaste={(e) => {
                      // if pasted URL with extension -> assume URL mode
                      const txt = e.clipboardData.getData('text');
                      if (isHttpUrl(txt)) {
                        setRemoteUrl(txt);
                        setMode('url');
                      }
                    }}
                  />
                </div>
                <div ref={memeRef} className="memeInputShell memeInputShellText">
                  <span className="memeInputShellIcon">Aa</span>
                  <textarea
                    placeholder="Meme caption (required)"
                    className="createMemeInput memeDescription memeInputTextarea"
                    ref={titleRef}
                    disabled={isLoading}
                    rows={3}
                    style={{ resize: 'vertical' }}
                  />
                </div>
                <div className="memeFieldHint">Add a short caption before publishing.</div>
                <PlayerTagPicker
                  selectedPlayers={selectedTaggedPlayers}
                  onChange={setSelectedTaggedPlayers}
                  token={player?.token}
                  currentPlayerId={player?.data?.player?.playerId}
                  currentPlayerMongoId={player?.data?.player?._id}
                  disabled={isLoading}
                />
                <div className="memeInputRow">
                  <input
                    ref={fileInputRef}
                    disabled={disableFileInput || isLoading}
                    type="file"
                    id="file"
                    className="memeFileInput"
                    accept=".jpg,.jpeg,.png,.gif,.webp,.avif,.heic,.heif,.mp4,.m4v,.mov,.webm,.ogg,.ogv,.flv,.mkv,.avi,.3gp,.3g2"
                    onChange={handleFileSelect}
                  />
                  {file && (
                    <button
                      type="button"
                      className="memeClearFileBtn"
                      disabled={isLoading}
                      onClick={removeSelectedFile}
                      title="Remove file"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {message && <div className="memeSuccess">{message}</div>}

                {error && (
                  <div className="messengerError_error">
                    <div className="memeErrorText">{error}</div>
                    <button
                      className="memeResetBtn"
                      type="button"
                      onClick={() => {
                        setError('');
                        if (!isLoading) resetForm();
                      }}
                    >
                      Reset
                    </button>
                  </div>
                )}

                {/* FILE PREVIEW */}
                {file &&
                  (filePreviewType === 'video' ? (
                    <div className="shareVidContainer">
                      <div className="memeMediaMeta">
                        Video selected: {file.name} ({formatBytes(file.size)})
                      </div>
                      <video className="memeVid" controls muted playsInline>
                        <source src={filePreviewUrl} type={file.type || 'video/mp4'} />
                      </video>
                      <Cancel
                        disabled={isLoading}
                        className="shareCancelVid"
                        onClick={removeSelectedFile}
                      />
                    </div>
                  ) : (
                    <div className="shareImgContainer">
                      <div className="memeMediaMeta">
                        Image selected: {file.name} ({formatBytes(file.size)})
                      </div>
                      <img
                        className="createMemeImg"
                        src={filePreviewUrl}
                        alt=""
                        draggable="false"
                      />
                      <Cancel
                        disabled={isLoading}
                        className="shareCancelImg"
                        onClick={removeSelectedFile}
                      />
                    </div>
                  ))}

                {/* REMOTE PREVIEW (if direct image/video) */}
                {!file && remoteUrl && remotePreviewType !== 'none' && (
                  <div className="remotePreviewBox">
                    <div className="memeMediaMeta">Remote source: {remoteHost}</div>
                    {remotePreviewType === 'image' && (
                      <img
                        src={remoteUrl}
                        alt="remote preview"
                        className="createMemeImg"
                        loading="lazy"
                        onError={() =>
                          setError('Could not load remote image preview.')
                        }
                      />
                    )}
                    {remotePreviewType === 'video' && (
                      <video
                        className="memeVid"
                        controls
                        muted
                        playsInline
                        preload="metadata"
                      >
                        <source src={remoteUrl} />
                      </video>
                    )}
                    <Cancel
                      disabled={isLoading}
                      className="shareCancelImg"
                      onClick={() => {
                        setRemoteUrl('');
                        if (urlInputRef.current) urlInputRef.current.value = '';
                        if (mode === 'url') setMode('idle');
                        setError('');
                      }}
                    />
                  </div>
                )}
                {!file && remoteUrl && remotePreviewType === 'none' && (
                  <div className="memeHint">
                    URL accepted. Preview unavailable for this host, but publish still works.
                  </div>
                )}

                {/* PROGRESS (file upload only) */}
                {file && isLoading && (
                  <div className="uploadProgressMeta">
                    <div className="uploadProgressBarOuter">
                      <div
                        className="uploadProgressBarInner"
                        style={{ width: `${uploadPct}%` }}
                      />
                    </div>
                    <span className="uploadProgressLabel">
                      {uploadPct}%{' '}
                      {uploadPct < 100 ? 'Uploading...' : 'Processing...'}
                    </span>
                  </div>
                )}

                <ConfettiTextComponent />

                <div className="memeActionsRow">
                  <button
                    className="animated-subscribe-btn memeButton"
                    type="submit"
                    disabled={isLoading}
                    title={
                      mode === 'idle'
                        ? 'Add a file or URL'
                        : isLoading
                        ? 'Uploading...'
                        : 'Share meme'
                    }
                  >
                    {isLoading ? (
                      <CircularProgress color="inherit" size="20px" />
                    ) : (
                      'Dale'
                    )}
                  </button>
                  {(file || remoteUrl) && !isLoading && (
                    <button
                      type="button"
                      className="memeSecondaryBtn"
                      onClick={resetForm}
                      title="Clear form"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* HINT / DND AREA */}
                {mode === 'idle' && !file && !remoteUrl && (
                  <div className="memeHint">
                    Paste, drag & drop, choose a file, or enter a URL
                    (image/video).
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
